import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRange } from '../../context/RangeContext';
import { useGame } from '../../context/GameContext';
import RangeMatrix from '../../components/RangeMatrix/RangeMatrix';
import { ButtonLarge } from '../../components/common/buttons/ButtonLarge/ButtonLarge';
import { QuizFooter, QuizFooterRow } from '../../components/common/QuizFooter';
import { formatTitle } from '../../utils/rangeFormatters';
import { BlastPageHeader } from '../../components/blast/BlastPageHeader/BlastPageHeader';
import { getRangeFilePath, formatBlastTitle, getBlastPageTitle } from '../BlastConfigScreen/blastRangeRules';

import './blast-view-range-screen.css';
import '../../components/RangeMatrix/range-matrix.css';

export const BlastViewRangeScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeRange, loadRange } = useRange();
  const { handleContinue } = useGame();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const rangeLoadedRef = useRef(false);

  const state = location.state || {};
  const config = state.config || {};
  const returnTo = state.returnTo || '/blast-answer';

  useEffect(() => {
    if (rangeLoadedRef.current) {
      setIsLoading(false);
      return;
    }

    const loadRangeData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const position = config.heroPosition || state.heroPosition || 'btn';
        const betType = config.betType || state.betType || 'open';

        if (!position) {
          throw new Error('Position is required');
        }

        const positionLower = position.toLowerCase();
        let rangePath;

        // Blast mode: resolve path via blastRangeRules
        const isBlast = state.quizType === 'BLAST' ||
                        config.quizType === 'BLAST' ||
                        config.format === '3way' || config.format === 'hu';

        if (isBlast) {
          const normalised = { ...config, position: config.position ?? config.heroPosition };
          const blastPath = getRangeFilePath(normalised);
          if (!blastPath) throw new Error('Could not resolve blast range path');
          rangePath = blastPath.startsWith('/') ? blastPath : `/${blastPath}`;
        } else if (config.range) {
          rangePath = config.range;
        } else if (betType === 'FACING_3BET_4BET') {
          const threeBetPos = config.threeBetPosition?.toLowerCase() || 'sb';
          rangePath = `/database/preflop/ranges/${positionLower}/${positionLower}_4bet-${threeBetPos}_3bet.csv`;
        } else if (betType === 'FACING_OPEN_3BET') {
          const openPos = config.openPosition?.toLowerCase() || 'hj';
          rangePath = `/database/preflop/ranges/${positionLower}/${positionLower}_3bet-${openPos}_open.csv`;
        } else {
          rangePath = `/database/preflop/ranges/${positionLower}/${positionLower}_open.csv`;
        }

        await loadRange({
          position: positionLower,
          betType: betType,
          rangePath: rangePath
        });

        rangeLoadedRef.current = true;
        setError(null);
      } catch (error) {
        console.error('Error loading range:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadRangeData();
  }, []);

  const handleBackToAnswer = () => {
    navigate(returnTo, {
      state: state
    });
  };

  const handleContinueClick = () => {
    console.log('BlastViewRangeScreen: Continue button clicked');
    handleContinue(state);
  };

  if (error) {
    return (
      <div className="blast-screen">
        <div className="blast-screen-content">
          <BlastPageHeader
            leftLink={{ label: 'Back', onClick: () => navigate(-1) }}
            rightLink={{ label: 'Reset', onClick: () => navigate('/blast-config') }}
            title={getBlastPageTitle(config?.format)}
          />
          <div className="error-message">
            {error}
          </div>
        </div>
      </div>
    );
  }

  const position = config.heroPosition || state.heroPosition || 'BTN';
  const betType = config.betType || state.betType || 'OPEN';
  const isBlastView = state.quizType === 'BLAST' ||
                      config.quizType === 'BLAST' ||
                      config.format === '3way' || config.format === 'hu';

  const getBlastSubtitle = () => {
    const stack = config.stack;
    return stack ? `${stack}bb` : '';
  };

  const getBlastTitleShort = () => formatBlastTitle(config);

  return (
    <div className="blast-screen">
      <div className={`blast-screen-content${isBlastView ? '' : ' range-view-content'}`}>

        {isBlastView && (
          <BlastPageHeader title={getBlastPageTitle(config?.format)} />
        )}

        <div className="view-range-header">
          <p className="view-range-subtitle">
            {isBlastView
              ? getBlastSubtitle()
              : 'Range View'
            }
          </p>
          <h1 className="view-range-title">
            {isBlastView
              ? getBlastTitleShort()
              : formatTitle(position, betType, {
                  openPosition: config?.openPosition,
                  threeBetPosition: config?.threeBetPosition,
                  mappedBetType: config?.mappedBetType,
                  callType: config?.callType
                })
            }
          </h1>
        </div>

        {isLoading ? (
          <div className="loading">Loading range...</div>
        ) : (
          <>
            <div className="range-matrix-fill">
              <RangeMatrix
                activeRange={activeRange}
                filterMode={config?.primaryAction !== undefined
                  ? config.filterMode
                  : (config?.filterMode ?? config?.subtype ?? null)}
                variant="view-range"
                size="fill"
              />
            </div>

            <QuizFooter horizontalPadding={16} rowGap={4}>
              <QuizFooterRow gap={4}>
                <ButtonLarge
                  text="Back to Answer"
                  buttonLargeClassName="button-large view-range-button"
                  onClick={handleBackToAnswer}
                />
                <ButtonLarge
                  text="Continue"
                  buttonLargeClassName="button-large continue-button"
                  onClick={handleContinueClick}
                />
              </QuizFooterRow>
            </QuizFooter>
          </>
        )}
      </div>
    </div>
  );
};

export default BlastViewRangeScreen;

